import React from 'react';
import {
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  interpolateColors,
} from 'remotion';
import { loadFont, fontFamily } from '@remotion/google-fonts/Inter';
import type { WordTiming } from '../types';

// Load Inter font with weights used in this component (700 and 800)
// This must be called at module level to ensure font is available
loadFont('normal', {
  subsets: ['latin'],
  weights: ['400', '700', '800'],
  ignoreTooManyRequestsWarning: true,
});

// Context-aware spring presets for different word types
const SPRING_PRESETS = {
  dramatic: { damping: 5, stiffness: 300, mass: 0.5 },
  keyword: { damping: 8, stiffness: 200, mass: 0.5 },
  regular: { damping: 20, stiffness: 200, mass: 0.5 },
};

// Default dramatic words (used by GDMQuietPhoneV5, PVMQuietPhoneV1, etc.)
const DEFAULT_DRAMATIC_WORDS = ['Why?', 'Free', 'Report.'];

interface DramaticWordsConfig {
  words: string[];
  matchMode: 'exact' | 'includes-ci';
}

// Get appropriate spring config based on word context
const getSpringConfig = (
  word: string,
  isLastWordInSentence: boolean,
  dramaticConfig: DramaticWordsConfig,
) => {
  const isDramatic = dramaticConfig.matchMode === 'includes-ci'
    ? dramaticConfig.words.some(dw => word.toLowerCase().includes(dw.toLowerCase()))
    : dramaticConfig.words.includes(word);

  if (isDramatic) {
    return SPRING_PRESETS.dramatic;
  }
  if (isLastWordInSentence) {
    return SPRING_PRESETS.keyword;
  }
  return SPRING_PRESETS.regular;
};

interface SyncedCaptionProps {
  words: WordTiming[];
  currentMs: number;
  primaryColor: string;
  accentColor: string;
  fontSize?: number;
  highlightCurrentWord?: boolean;
  layout?: 'center' | 'lines';
  dramaticWords?: string[];
  dramaticMatchMode?: 'exact' | 'includes-ci';
  overrideFirstWordColor?: boolean;
}

/**
 * Displays captions with perfect sync to word-level timing
 * Words appear exactly when they're spoken
 *
 * IMPORTANT: currentMs must be the absolute composition time, not scene-relative time.
 * This component should be rendered OUTSIDE of TransitionSeries to get accurate timing.
 */
export const SyncedCaption: React.FC<SyncedCaptionProps> = ({
  words,
  currentMs,
  primaryColor,
  accentColor,
  fontSize = 56,
  highlightCurrentWord = true,
  layout = 'center',
  dramaticWords = DEFAULT_DRAMATIC_WORDS,
  dramaticMatchMode = 'exact',
  overrideFirstWordColor = true,
}) => {
  const { width, height } = useVideoConfig();
  const isPortrait = height / width >= 1.6;

  const dramaticConfig: DramaticWordsConfig = {
    words: dramaticWords,
    matchMode: dramaticMatchMode,
  };

  // Group words into lines for better display
  const getWordLines = () => {
    const lines: WordTiming[][] = [];
    let currentLine: WordTiming[] = [];

    for (const word of words) {
      currentLine.push(word);
      if (word.text.endsWith('.') || word.text.endsWith('?')) {
        lines.push([...currentLine]);
        currentLine = [];
      }
    }

    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    return lines;
  };

  const lines = layout === 'lines' ? getWordLines() : [words];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        padding: isPortrait ? '10% 12%' : '8%',
        fontFamily,
        gap: 16,
      }}
    >
      {lines.map((lineWords, lineIndex) => {
        // Check if any word in this line should be visible
        const lineVisible = lineWords.some(word => currentMs >= word.startMs);
        if (!lineVisible) return null;

        return (
          <div
            key={lineIndex}
            style={{
              backgroundColor: 'rgba(0,0,0,0.35)',
              padding: '24px 40px',
              borderRadius: 16,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'baseline',
              gap: '0.3em',
              maxWidth: isPortrait ? '70%' : '90%',
            }}
          >
            {lineWords.map((word, wordIndex) => (
              <AnimatedWord
                key={`${lineIndex}-${wordIndex}`}
                word={word}
                currentMs={currentMs}
                primaryColor={primaryColor}
                accentColor={accentColor}
                fontSize={fontSize}
                highlightCurrentWord={highlightCurrentWord}
                isLastWordInSentence={word.text.endsWith('.') || word.text.endsWith('?')}
                dramaticConfig={dramaticConfig}
                overrideFirstWordColor={overrideFirstWordColor}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

interface AnimatedWordProps {
  word: WordTiming;
  currentMs: number;
  primaryColor: string;
  accentColor: string;
  fontSize: number;
  highlightCurrentWord: boolean;
  isLastWordInSentence: boolean;
  dramaticConfig: DramaticWordsConfig;
  overrideFirstWordColor: boolean;
}

const AnimatedWord: React.FC<AnimatedWordProps> = ({
  word,
  currentMs,
  primaryColor,
  accentColor,
  fontSize,
  highlightCurrentWord,
  isLastWordInSentence,
  dramaticConfig,
  overrideFirstWordColor,
}) => {
  const { fps } = useVideoConfig();
  const isFirstWord = word.startMs <= 200;
  const effectivePrimary = overrideFirstWordColor && isFirstWord ? '#FFFFFF' : primaryColor;
  const effectiveAccent = overrideFirstWordColor && isFirstWord ? '#FFFFFF' : accentColor;

  // Word is visible if current time is past its start time
  const isVisible = currentMs >= word.startMs;
  const isCurrent = currentMs >= word.startMs && currentMs < word.endMs;
  const isPast = currentMs >= word.endMs;

  if (!isVisible) {
    return null;
  }

  // Calculate animation progress based on word timing
  const progressMs = currentMs - word.startMs;

  // Spring animation for pop-in effect with context-aware config
  const framesSinceStart = Math.round((progressMs / 1000) * fps);
  const springConfig = getSpringConfig(word.text, isLastWordInSentence, dramaticConfig);
  const scale = spring({
    frame: framesSinceStart,
    fps,
    config: springConfig,
  });

  // Determine word styling
  const isHighlighted = highlightCurrentWord && isCurrent;
  const isDramaticWord = dramaticConfig.matchMode === 'includes-ci'
    ? dramaticConfig.words.some(dw => word.text.toLowerCase().includes(dw.toLowerCase()))
    : dramaticConfig.words.includes(word.text);
  const isKeyWord = isLastWordInSentence || isDramaticWord;

  // Frame-based color interpolation (replaces CSS transition)
  const targetColor = isHighlighted || (isKeyWord && isPast) ? effectiveAccent : effectivePrimary;
  const previousColor = effectivePrimary;

  // Animate color transition over 6 frames (~0.2s at 30fps)
  const colorTransitionFrames = Math.round(fps * 0.2);
  const colorProgress = interpolate(
    framesSinceStart,
    [0, colorTransitionFrames],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  // Use interpolateColors for smooth frame-based color animation
  const animatedColor = isHighlighted || (isKeyWord && isPast)
    ? interpolateColors(colorProgress, [0, 1], [previousColor, targetColor])
    : effectivePrimary;

  // Opacity: current word at full, past words slightly dimmed
  const opacity = isCurrent ? 1 : isPast ? 0.9 : 1;

  // Scale effect - larger for dramatic words, normal bump for current word
  const baseScale = isDramaticWord ? 1.3 : isKeyWord ? 1.1 : 1;
  const currentScale = isCurrent ? 1.05 : 1;

  // Y offset for pop-in - more dramatic for key words
  const popInDistance = isDramaticWord ? 25 : 15;
  const translateY = interpolate(
    scale,
    [0, 1],
    [popInDistance, 0],
    { extrapolateRight: 'clamp' }
  );

  // Glow effect for dramatic words (frame-based, not CSS)
  const glowIntensity = isDramaticWord
    ? interpolate(
        Math.sin(framesSinceStart * 0.3),
        [-1, 1],
        [40, 60],
        { extrapolateRight: 'clamp' }
      )
    : 40;

  const textShadow = isHighlighted || isDramaticWord
    ? `0 0 ${glowIntensity}px ${effectiveAccent}, 0 4px 30px rgba(0,0,0,1)`
    : '0 4px 30px rgba(0,0,0,1), 0 2px 4px rgba(0,0,0,0.9)';

  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: fontSize * baseScale,
        fontWeight: isKeyWord ? 800 : 700,
        color: animatedColor,
        opacity,
        transform: `translateY(${translateY}px) scale(${scale * currentScale})`,
        textShadow,
        WebkitTextStroke: '0.5px rgba(0,0,0,0.4)',
        paintOrder: 'stroke fill',
        marginRight: '0.35em',
      }}
    >
      {word.text}
    </span>
  );
};

/**
 * Alternative caption style: one line at a time
 */
interface SingleLineCaptionProps {
  words: WordTiming[];
  primaryColor: string;
  fontSize?: number;
}

export const SingleLineCaption: React.FC<SingleLineCaptionProps> = ({
  words,
  primaryColor,
  fontSize = 64,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const currentMs = (frame / fps) * 1000;

  // Find current sentence (group of words ending with . or ?)
  const sentences: WordTiming[][] = [];
  let current: WordTiming[] = [];

  for (const word of words) {
    current.push(word);
    if (word.text.endsWith('.') || word.text.endsWith('?')) {
      sentences.push([...current]);
      current = [];
    }
  }
  if (current.length > 0) {
    sentences.push(current);
  }

  // Find which sentence is currently being spoken
  const currentSentence = sentences.find(sentence => {
    const start = sentence[0].startMs;
    const end = sentence[sentence.length - 1].endMs;
    return currentMs >= start && currentMs < end + 500; // +500ms grace period
  });

  if (!currentSentence) return null;

  const sentenceText = currentSentence.map(w => w.text).join(' ');

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        right: '5%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily,
      }}
    >
      <span
        style={{
          fontSize,
          fontWeight: 700,
          color: primaryColor,
          textAlign: 'center',
          textShadow: '0 4px 20px rgba(0,0,0,0.8)',
          lineHeight: 1.2,
        }}
      >
        {sentenceText}
      </span>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
